import { useState } from "react";
import {
  Group,
  Loader,
  Space,
  Text,
  TextInput,
  rem,
  Image,
  Checkbox,
  Paper,
  Button,
  Box,
  Switch,
} from "@mantine/core";
import { IconSearch, IconShare } from "@tabler/icons-react";
import { MeterReturnType, MetersReturnType, RowData } from "../../types";
import { formatNumberWithCommas } from "../../utils/numbers";
import { formatDate } from "../../utils/date";
import CustomTable from "../../components/tables/CustomTable";

type MeterTableProps = {
  selectedMeter: MeterReturnType | null;
  meters: MetersReturnType;
  loadingMeters: boolean;
  handleMeterSelect: (meter: MeterReturnType | null) => void;
  onMenuClick: (meter: MeterReturnType) => void;
  onAllReadingsClick: (meter: MeterReturnType) => void;
  selectedMetersForSharing: MeterReturnType[];
  toggleMeterForSharing: (meter: MeterReturnType) => void;
  onShareClick: () => void;
};

export default function MeterTable({
  meters,
  loadingMeters,
  handleMeterSelect,
  onMenuClick,
  onAllReadingsClick,
  selectedMeter,
  selectedMetersForSharing,
  toggleMeterForSharing,
  onShareClick,
}: MeterTableProps) {
  const [showLiters, setShowLiters] = useState(false);
  const [search, setSearch] = useState("");

  const headers = [
    { label: <IconShare size={18} stroke={1.5} fill="currentColor" />, accessor: "shareSelect" },
    { label: "Meter No.", accessor: "meterNumber" },
    {
      label: (
        <Box>
          <Switch
            size="xs"
            label={showLiters ? "liters" : "units"}
            checked={showLiters}
            onChange={(e) => setShowLiters(e.currentTarget.checked)}
            styles={{ label: { paddingLeft: 4, fontSize: 11 } }}
          />
          <Text size="sm" fw={700} mt={4}>
            {showLiters ? "Last Reading(l)" : "Last Reading"}
          </Text>
        </Box>
      ),
      accessor: "lastReading",
    },
    { label: "Last Upload", accessor: "lastUpload" },
    { label: "Battery", accessor: "battery" },
    { label: "", accessor: "menu" },
  ];

  const filteredMeters = (meters || []).filter((meter) =>
    meter.meterSerialNo.toLowerCase().includes(search.toLowerCase())
  );

  const data = filteredMeters.map((meter) => ({
    id: meter.id,
    shareSelect: "__commonData__",
    meterNumber: {
      component: (
        <Group>
          <Checkbox
            checked={selectedMeter?.id === meter?.id ? true : false}
            readOnly
          />
          <Image
            src={"../assets/images/tap-icon.png"}
            alt="tap"
            width={24}
            height={24}
          />
          <Box>
            <Text size="sm">{meter.meterSerialNo}</Text>
            {meter.labelDetails && (
              <Text size="xs" c="dimmed" fz={10}>
                {meter.labelDetails}
              </Text>
            )}
          </Box>
        </Group>
      ),
      hoverableCell: true,
      onCellClick: () => {
        handleMeterSelect(meter);
      },
    },
    lastReading: (() => {
      const volume = meter.Aggregate_smartMeterData[0]?.totalVolume;
      if (!(volume >= 0)) return <Text size="sm">--</Text>;
      if (showLiters) return <Text size="sm">{formatNumberWithCommas(volume)}</Text>;
      const truncated = Math.trunc(volume / 10) / 100;
      const [whole, dec] = truncated.toFixed(2).split(".");
      return (
        <Group gap={0}>
          <Text size="sm" fw={700}>{formatNumberWithCommas(Number(whole))}</Text>
          <Text size="xs" fw={400} c="dimmed">.{dec}</Text>
        </Group>
      );
    })(),
    lastUpload: (
      <Text size="sm">
        {meter.Aggregate_smartMeterData[0]?.deviceTime
          ? formatDate(meter.Aggregate_smartMeterData[0]?.deviceTime)
          : "--"}
      </Text>
    ),
    battery: (
      <Text size="sm">
        {meter.Aggregate_smartMeterData[0]?.battery
          ? `${meter.Aggregate_smartMeterData[0]?.battery}%`
          : "--"}
      </Text>
    ),
    menu: "__commonData__",
    hoverableCell: true,
    rawData: meter as any,
  }));

  const commonData = {
    shareSelect: (rowData: RowData) => {
      const meter = rowData?.rawData as unknown as MeterReturnType;
      const isSelected = selectedMetersForSharing.some((m) => m.id === meter.id);
      return (
        <Checkbox
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();
            toggleMeterForSharing(meter);
          }}
          onClick={(e) => e.stopPropagation()}
        />
      );
    },
    menu: (rowData: RowData) => (
      <Group gap={4} wrap="nowrap" justify="flex-end">
        <Button
          size="compact-xs"
          variant="light"
          onClick={(e) => {
            e.stopPropagation();
            onAllReadingsClick(rowData?.rawData as unknown as MeterReturnType);
          }}
        >
          All readings
        </Button>
        <Button
          size="compact-xs"
          variant="subtle"
          onClick={(e) => {
            e.stopPropagation();
            onMenuClick(rowData?.rawData as unknown as MeterReturnType);
          }}
        >
          Meter details
        </Button>
      </Group>
    ),
  };

  return (
    <Box pos="relative">
      <Group justify="space-between">
        <Text c="#03045e" size="xl" fw={800}>
          Meters
        </Text>
        <Group>
          {selectedMetersForSharing.length > 0 && (
            <Button
              leftSection={<IconShare size={16} />}
              onClick={onShareClick}
              size="sm"
            >
              Share ({selectedMetersForSharing.length})
            </Button>
          )}
          <TextInput
            variant="filled"
            leftSection={<IconSearch size={20} stroke={1.5} />}
            w={rem(250)}
            placeholder="Search meter no."
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            styles={() => ({
              input: {
                backgroundColor: "#f5f9fd",
              },
            })}
          />
        </Group>
      </Group>
      <Space h="sm" />
      {loadingMeters ? (
        <Group justify="center" my={"md"}>
          <Loader size={"sm"} />
          <Text>Loading...</Text>
        </Group>
      ) : !data?.length ? (
        <Paper py={"md"} radius={"md"}>
          <Text ta={"center"} fw={600}>
            No meters found for this property.
          </Text>
        </Paper>
      ) : (
        <CustomTable headers={headers} rows={data} commonData={commonData} />
      )}
    </Box>
  );
}
